import type { Option } from "@tento-lona/sheets";
import { Duration, TimeOfDay, TimezoneRegion } from "@tento-chrono";
import type { FixedChildSpec } from "@tento-ui/components/sheet/fixed-child-culling";
import { StickerArtCatalog } from "@tento-ui/components/sticker/sticker-art-catalog";
import { StickerCutoutPreview } from "@tento-ui/components/sticker/sticker-cutout-preview";
import type { StickerEffect } from "@tento-ui/components/sticker/sticker-effect";
import type { StickerMaterialVariant } from "@tento-ui/components/sticker/sticker-material-variant";
import type { CalendarSubcolumn } from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import type { StickerSlot } from "./sticker-gesture-item.ts";
import { CellId } from "@tento-lona/sheets";

/**
 * Sheet-level sticker rendering: the visible sticker cutout paints in the
 * sheet's fixed-child layer so it escapes
 * the day column's clipping and stacking, while the invisible span cell
 * inside the timeline[column] grid stays the position anchor and
 * gesture surface. Each spec's wrapper occupies the sticker's 30-minute
 * slot and centers the (possibly larger) artwork on it — positioned
 * with the same pixel constants as that cell's layout.
 */
export namespace StickerFixedChildren {
  /** [calendar-padding] + [divider] above the timed grid — matches
   *  `RendererGestureHost.offsetY`. */
  const COLUMN_TOP_PX = 12 + 1;
  const PX_PER_HOUR = 48;
  /** Stickers are instant-only `[start, start + 30min]` — the slot the
   *  anchor cell occupies and the artwork centers within. */
  const SLOT_HEIGHT_PX = (30 / 60) * PX_PER_HOUR;
  const RESTING_FIXED_CHILD_Z_INDEX =
    "var(--sheet-fixed-child-z-index, 16777266)";
  const RAISED_FIXED_CHILD_Z_INDEX =
    "var(--sticker-raised-z-index, 16777267)";

  export function specId(rowId: string, cellId: CellId): string {
    return `sticker_cut_${sanitize(rowId)}_${sanitize(cellId)}`;
  }

  /** Per-overlay wiring hook. Called with the rendered inner sticker
   *  element on first build and every re-render, so the host can make it
   *  the sticker's drag surface (the overlay — not the in-column anchor —
   *  owns move + details, so a grab on the cross-border overhang still
   *  hits the sticker). */
  export type OverlayHook = (slot: StickerSlot, $inner: HTMLElement) => void;

  export type Visual = {
    stickerId: string;
    effect: StickerEffect.Id;
    effectVariant: Option<StickerMaterialVariant.Id>;
    x: number;
  };

  export function makeSpecs(
    rowId: string,
    slots: readonly StickerSlot[],
    context: CalendarSubcolumn.FixedChildrenContext,
    onInner: OverlayHook,
  ): FixedChildSpec[] {
    const tz = context.renderedColumns.start.tz;
    return slots.map((slot) =>
      makeSpec(rowId, slot, tz, context.validHours, onInner),
    );
  }

  /** Inverse of a resting overlay's vertical placement, for the drag-ghost
   *  drop. Given a column-local y where the dragged sticker's CENTER sits,
   *  return the `offset.y` to feed `CalendarColumnCell.PointerPosition.resolve`
   *  so the persisted sticker re-renders with its center back at exactly
   *  that y. Cancels the column-top padding + half-slot centering this
   *  module's `makeSpec`/`positionInner` add, so a drop never shifts the
   *  sticker from where the ghost was released. */
  export function resolveOffsetY(centerYInColumn: number): number {
    return centerYInColumn - COLUMN_TOP_PX - SLOT_HEIGHT_PX / 2;
  }

  function makeSpec(
    rowId: string,
    slot: StickerSlot,
    tz: TimezoneRegion,
    validHours: TimeOfDay.Range,
    onInner: OverlayHook,
  ): FixedChildSpec {
    const local = tz.toTz(slot.timeRange.start);
    const top = Math.round(
      COLUMN_TOP_PX + slotOffsetY(local.time.duration(), validHours),
    );
    const render = ($wrap: HTMLElement): void => {
      // Resting stickers lie flat on the sheet and are their own drag
      // surface, so grabs land on the drawn artwork wherever it is — including
      // the part that overhangs into the neighboring day.
      const contextMenuOpen = $wrap.hasAttribute("context-menu-open");
      const $inner = renderVisual(
        $wrap,
        slot,
        contextMenuOpen ? "raised" : "flat",
      );
      $inner.style.pointerEvents = "auto";
      $inner.style.cursor = "grab";
      setContextMenuOpen($inner, contextMenuOpen);
      onInner(slot, $inner);
    };
    return {
      id: specId(rowId, slot.cellId),
      x: { type: "date-range", startDate: local.date, endDate: local.date },
      inset: { left: 0, top },
      widthBehavior: "fill",
      makeElement: () => {
        const $wrap = makeWrap();
        render($wrap);
        return $wrap;
      },
      onUpdate: ($wrap) => {
        render($wrap);
      },
    };
  }

  /** Pixels from the timed grid's top to the start of the sticker's
   *  slot, clamped to the visible-hours window start. */
  function slotOffsetY(tod: Duration.Time, validHours: TimeOfDay.Range): number {
    const windowStart = validHours.start.duration();
    if (tod.toMs <= windowStart.toMs) return 0;
    return tod.sub(windowStart).hrsF * PX_PER_HOUR;
  }

  function makeWrap(): HTMLElement {
    const $wrap = document.createElement("div");
    // Spans the day column (widthBehavior "fill"); the sticker is
    // absolutely positioned within it at the slot's horizontal fraction.
    $wrap.style.position = "relative";
    $wrap.style.height = `${SLOT_HEIGHT_PX}px`;
    // Gestures stay on the anchor cell — the drawn sticker never
    // intercepts the pointer.
    $wrap.style.pointerEvents = "none";
    $wrap.style.zIndex = RESTING_FIXED_CHILD_Z_INDEX;
    return $wrap;
  }

  export function setContextMenuOpen(
    $inner: HTMLElement,
    open: boolean,
  ): void {
    $inner.toggleAttribute("context-menu-open", open);
    setVisualDepth($inner, open ? "raised" : "flat");

    const $wrap = $inner.parentElement instanceof HTMLElement
      ? $inner.parentElement
      : null;
    if (!$wrap) return;
    $wrap.toggleAttribute("context-menu-open", open);
    $wrap.style.zIndex = open
      ? RAISED_FIXED_CHILD_Z_INDEX
      : RESTING_FIXED_CHILD_Z_INDEX;
  }

  /**
   * Render the sticker's visual cutout into
   * `$host`, absolutely positioned at the slot's horizontal fraction and
   * vertically centered. `$host` must be a positioning context (the
   * fixed-child wrapper, or the in-column drag-ghost cell). Shared so the
   * dragged ghost is the exact same sticker as the resting overlay.
   */
  export function renderVisual(
    $host: HTMLElement,
    slot: StickerSlot,
    depth: StickerCutoutPreview.Depth,
  ): HTMLElement {
    return renderFloatingVisual($host, {
      stickerId: slot.stickerId,
      effect: slot.effect,
      effectVariant: null,
      x: slot.x,
    }, depth);
  }

  export function renderFloatingVisual(
    $host: HTMLElement,
    visual: Visual,
    depth: StickerCutoutPreview.Depth,
  ): HTMLElement {
    const $preview = reuseChild(
      $host,
      (e): e is StickerCutoutPreview => e instanceof StickerCutoutPreview,
      () => StickerCutoutPreview.make(),
    );
    // Position (x) is cheap and may change every frame; the cutout bind
    // clears + re-applies the image, so only re-bind when the sticker or
    // depth actually changed — otherwise a dragging ghost (re-emitted
    // each frame) flickers.
    positionInner($preview, visual.x);
    const boundKey =
      `${visual.stickerId}:${depth}:${visual.effect}:${visual.effectVariant ?? ""}`;
    if ($preview.dataset.boundKey !== boundKey) {
      $preview.dataset.boundKey = boundKey;
      void StickerArtCatalog.asset(visual.stickerId).then((asset) => {
        if (!asset || $preview.dataset.boundKey !== boundKey) return;
        void StickerArtCatalog.cutout(asset.id).then((result) => {
          if (!result || $preview.dataset.boundKey !== boundKey) return;
          const displaySize = StickerArtCatalog.resolveDisplaySize(asset);
          $preview.bindResult({
            result,
            alt: asset.alt,
            imageRendering: asset.imageRendering,
            displayWidth: displaySize.width,
            displayHeight: displaySize.height,
            depth,
            effect: visual.effect,
            effectVariant: visual.effectVariant,
          });
        });
      });
    }
    return $preview;
  }

  /** Center the sticker on (x-fraction across the column, slot center).
   *  Overflow is visible, so a sticker larger than the slot — or sitting
   *  near a column edge — overhangs symmetrically and may cross into the
   *  neighboring day. That cross-border overhang is intentional: a
   *  sticker is a free-floating decoration, not a column-bound cell, and
   *  it stays grabbable there because the overlay artwork is its own
   *  drag surface (see the overlay drag source), not the in-column
   *  anchor span. */
  function positionInner($inner: HTMLElement, x: number): void {
    $inner.style.position = "absolute";
    $inner.style.left = `${x * 100}%`;
    $inner.style.top = "50%";
    $inner.style.transform = "translate(-50%, -50%)";
  }

  function setVisualDepth(
    $inner: HTMLElement,
    depth: StickerCutoutPreview.Depth,
  ): void {
    $inner.setAttribute("depth", depth);
    $inner.style.filter = StickerCutoutPreview.DROP_SHADOW[depth] ?? "";
  }

  /** Reuse the wrapper's child when it already matches, else replace it
   *  — keeps the cull layer's element recycling cheap across rebinds. */
  function reuseChild<T extends HTMLElement>(
    $wrap: HTMLElement,
    isMatch: (e: Element) => e is T,
    make: () => T,
  ): T {
    const existing = $wrap.firstElementChild;
    if (existing && isMatch(existing)) return existing;
    const created = make();
    $wrap.replaceChildren(created);
    return created;
  }

  function sanitize(value: string): string {
    return value.replace(/[^a-zA-Z0-9_]/g, "_");
  }
}
